import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setMode,
    setZoom,
    setPan,
    addScreenAtCenter,
    setGridColor,
} from './utils/state/canvasSlice';
import { createScreen, createUIElement } from './utils/state/screensSlice';
import { addScreenToExperience } from './utils/state/experienceSlice';
import DeviceSelector from './DeviceSelector';
import UndoRedoToolbar from './UndoRedoToolbar';
import {
    MousePointer,
    Hand,
    ZoomIn,
    ZoomOut,
    Move,
    Grid3X3,
    Plus,
    Route,
    Info,
    BookOpen,
    X,
    Download,
    Upload,
    Save,
    Copy,
    HelpCircle,
} from 'lucide-react';
import moment from 'moment';
import { Loader2 } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';

interface CanvasToolbarProps {
    onToggleElementPalette?: () => void;
    showElementPalette?: boolean;
    showConnections?: boolean;
    onToggleConnections?: () => void;
    onToggleLegend?: () => void;
    onToggleTemplates?: () => void;
    selectedScreenId?: string;
    onExitEditor?: () => void;
    onCreateScreen?: (screenData: Partial<ScreenDefinition>) => void;
    canvasWidth?: number;
    canvasHeight?: number;
    onExport?: () => void;
    onImport?: () => void;
    onSave?: () => void;
    hasUnsavedChanges?: boolean;
    lastSaved?: Date;
    submitting?: boolean;
    replicateMode?: boolean;
    onToggleReplicate?: () => void;
    activationId?: number;
    onToggleHelp?: () => void;
}

export default function CanvasToolbar({
    showConnections = true,
    onToggleConnections,
    onToggleLegend,
    onToggleTemplates,
    selectedScreenId,
    onExitEditor,
    onCreateScreen,
    canvasWidth = 1200,
    canvasHeight = 800,
    onExport,
    onImport,
    onSave,
    hasUnsavedChanges = false,
    lastSaved,
    submitting = false,
    replicateMode = false,
    onToggleReplicate,
    activationId,
    onToggleHelp,
}: CanvasToolbarProps) {
    const dispatch = useDispatch();
    const { zoom, mode, gridColor } = useSelector((state: any) => state.canvas);

    const handleModeChange = (newMode: 'select' | 'pan' | 'connect') => {
        dispatch(setMode(newMode));
    };

    const handleZoomIn = () => {
        dispatch(setZoom(Math.min(3, zoom + 0.2)));
    };

    const handleZoomOut = () => {
        dispatch(setZoom(Math.max(0.1, zoom - 0.2)));
    };

    const handleZoomReset = () => {
        dispatch(setZoom(1));
        dispatch(setPan({ x: 0, y: 0 }));
    };

    const handleFitToScreen = () => {
        // TODO: Calculate bounds of all screens and fit to view
        dispatch(setZoom(0.8));
        dispatch(setPan({ x: 50, y: 50 }));
    };

    const handleGridColorChange = (color: string) => {
        dispatch(setGridColor(color));
    };

    return (
        <div className="w-full bg-background border-b border-border p-2 flex items-center justify-between shadow-sm">
            {/* Left side tools */}
            <div className="flex items-center gap-1">
                {/* Mode tools */}
                <div className="flex items-center gap-1">
                    <Button
                        variant={mode === 'select' ? 'default' : 'ghost'}
                        size="icon-sm"
                        onClick={() => handleModeChange('select')}
                        title="Select Tool (V)"
                    >
                        <MousePointer className="w-4 h-4" />
                    </Button>
                    <Button
                        variant={mode === 'pan' ? 'default' : 'ghost'}
                        size="icon-sm"
                        onClick={() => handleModeChange('pan')}
                        title="Pan Tool (H)"
                    >
                        <Hand className="w-4 h-4" />
                    </Button>
                    <Button
                        variant={mode === 'connect' ? 'default' : 'ghost'}
                        size="icon-sm"
                        onClick={() => handleModeChange('connect')}
                        title="Connect Tool (C)"
                    >
                        <Move className="w-4 h-4" />
                    </Button>
                </div>

                <div className="w-px h-6 bg-muted"></div>

                {/* Zoom tools */}
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleZoomOut}
                        title="Zoom Out (-)"
                    >
                        <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-xs font-mono min-w-[3rem] text-center">
                        {Math.round(zoom * 100)}%
                    </span>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleZoomIn}
                        title="Zoom In (+)"
                    >
                        <ZoomIn className="w-4 h-4" />
                    </Button>
                </div>

                <div className="w-px h-6 bg-muted"></div>

                {/* View tools */}
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleZoomReset}
                        title="Reset Zoom (0)"
                    >
                        <Grid3X3 className="w-4 h-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleFitToScreen}
                        title="Fit to Screen (F)"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                            />
                        </svg>
                    </Button>
                </div>

                <div className="w-px h-6 bg-muted"></div>

                {/* Grid Color Picker */}
                <div className="flex items-center gap-1">
                    <label className="text-xs text-muted-foreground font-medium">
                        Grid:
                    </label>
                    <div className="relative">
                        <input
                            type="color"
                            value={gridColor}
                            onChange={(e) =>
                                handleGridColorChange(e.target.value)
                            }
                            className="w-8 h-8 rounded border border-border cursor-pointer"
                            title="Grid Color"
                        />
                    </div>
                </div>

                <div className="w-px h-6 bg-muted"></div>

                {/* Device Selector */}
                <DeviceSelector />

                <div className="w-px h-6 bg-muted"></div>

                {/* Undo/Redo Controls */}
                <UndoRedoToolbar className="" showHistoryButton={true} />

                <div className="w-px h-6 bg-muted"></div>

                {/* Navigation connections toggle */}
                <Button
                    variant={showConnections ? 'default' : 'ghost'}
                    size="icon-sm"
                    title="Toggle Navigation Flow (N)"
                    onClick={onToggleConnections}
                >
                    <Route className="w-4 h-4" />
                </Button>

                {/* Legend toggle */}
                <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Show Legend (L)"
                    onClick={onToggleLegend}
                >
                    <Info className="w-4 h-4" />
                </Button>

                {/* Template library */}
                <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Screen Templates (T)"
                    onClick={onToggleTemplates}
                >
                    <BookOpen className="w-4 h-4" />
                </Button>

                {/* Help */}
                <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Help & Documentation (H)"
                    onClick={onToggleHelp}
                >
                    <HelpCircle className="w-4 h-4" />
                </Button>

                {/* Replicate tool */}
                <Button
                    variant={replicateMode ? 'default' : 'ghost'}
                    size="icon-sm"
                    className={replicateMode ? 'bg-orange-500 hover:bg-orange-600' : ''}
                    title="Replicate Tool - Copy individual properties to other screens/elements (R)"
                    onClick={onToggleReplicate}
                >
                    <Copy className="w-4 h-4" />
                </Button>

                {/* Add screen tool */}
                <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Add Screen (Shift+A)"
                    onClick={() => {
                        const screenId = `screen-${Date.now()}`;
                        const newScreen = {
                            id: screenId,
                            displayProperties: {
                                backgroundColor: '#FFFFFF',
                                backgroundMediaUrl: '',
                                foregroundColor: '#000000',
                                buttonColor: '#3B82F6',
                                buttonTextColor: '#FFFFFF',
                                horizontalPadding: 0,
                                verticalPadding: 0,
                            },
                            stage: {
                                type: 'attract' as any,
                                metadata: {},
                            },
                            elements: [],
                            navigateDestination: undefined,
                            navigateAfterTimeout: undefined,
                            metadata: {},
                        };

                        if (onCreateScreen) {
                            onCreateScreen(newScreen);
                        } else {
                            // Fallback to direct dispatch if no handler provided
                            dispatch(createScreen(newScreen));
                            // Add screen to the experience's screen list
                            if (activationId !== undefined) {
                                dispatch(
                                    addScreenToExperience({
                                        experienceId: activationId,
                                        screenId: screenId,
                                    }),
                                );
                            }
                        }

                        // Position the new screen at the center of the canvas
                        dispatch(
                            addScreenAtCenter({
                                screenId,
                                canvasWidth,
                                canvasHeight,
                            }),
                        );
                    }}
                >
                    <Plus className="w-4 h-4" />
                </Button>

                {/* Quick test elements button (for demo) */}
                {selectedScreenId && (
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-green-700 hover:bg-green-200"
                        title="Add Test Elements"
                        onClick={() => {
                            const testElements = [
                                {
                                    type: 'text' as UIElementType,
                                    h: 'center',
                                    v: 'top',
                                },
                                {
                                    type: 'button' as UIElementType,
                                    h: 'center',
                                    v: 'center',
                                },
                                {
                                    type: 'image' as UIElementType,
                                    h: 'leading',
                                    v: 'center',
                                },
                                {
                                    type: 'liveView' as UIElementType,
                                    h: 'trailing',
                                    v: 'center',
                                },
                                {
                                    type: 'currentSessionQR' as UIElementType,
                                    h: 'center',
                                    v: 'bottom',
                                },
                            ];

                            testElements.forEach(({ type, h, v }) => {
                                dispatch(
                                    createUIElement({
                                        screenId: selectedScreenId,
                                        horizontalLocation: h,
                                        verticalLocation: v,
                                    }),
                                );
                            });
                        }}
                    >
                        🧪
                    </Button>
                )}
            </div>

            {/* Right side tools */}
            <div className="flex items-center gap-1">
                {/* File Operations */}
                <div className="flex items-center gap-1">
                    {/* Export */}
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        title="Export Experience (Ctrl+E)"
                        onClick={onExport}
                    >
                        <Download className="w-4 h-4" />
                    </Button>

                    {/* Import */}
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        title="Import Experience (Ctrl+I)"
                        onClick={onImport}
                    >
                        <Upload className="w-4 h-4" />
                    </Button>

                    {/* Save */}
                    <Button
                        variant={hasUnsavedChanges ? 'default' : 'ghost'}
                        size="icon-sm"
                        title="Save Experience (Ctrl+S)"
                        onClick={onSave}
                        disabled={!hasUnsavedChanges || submitting}
                    >
                        <Save className="w-4 h-4" />
                    </Button>

                    {/* Last Saved Indicator */}
                    {lastSaved && (
                        <div className="text-xs text-muted-foreground px-2">
                            {submitting ? (
                                <span className="text-blue-600 inline-flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Saving...
                                </span>
                            ) : (
                                <span
                                    title={`Last saved: ${moment(
                                        lastSaved,
                                    ).format('MMMM Do YYYY, h:mm:ss a')}`}
                                >
                                    Saved {moment(lastSaved).fromNow()}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Separator */}
                <div className="w-px h-6 bg-muted"></div>

                {/* Exit Editor */}
                <Button
                    variant="destructive"
                    size="sm"
                    title="Exit Visual Editor (Esc)"
                    onClick={() => onExitEditor?.()}
                >
                    <X className="w-4 h-4" />
                    <span>Exit</span>
                </Button>
            </div>
        </div>
    );
}
